import { useQuery } from '@tanstack/react-query';
import { Card } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { formatDate } from '@/shared/utils/format';
import { registryApi, type SkillChange, type SkillChangeStatus } from '../../api/registryApi';
import { registryKeys } from '../../queryKeys';
import { CHANGE_KIND_LABELS, CHANGE_STATUS_LABELS } from '../../utils/skillEditor';
import styles from './SkillChangesPanel.module.css';

const STATUS_CLASS: Record<SkillChangeStatus, string> = {
  pr_open: patterns.pillInfo,
  merged: patterns.pillSuccess,
  closed: patterns.pillMuted,
};

export function ChangeStatusPill({ change }: { change: SkillChange }) {
  return (
    <span className={STATUS_CLASS[change.status]}>
      {change.prNumber ? `#${change.prNumber} · ` : ''}
      {CHANGE_STATUS_LABELS[change.status]}
    </span>
  );
}

/**
 * The pull requests proposed from the registry — every one for the agency, or one skill's history.
 * Open rows are brought up to date with GitHub as they are read (throttled server-side).
 */
export function SkillChangesPanel({
  owner,
  dirName,
  title = 'Proposed changes',
  bare = false,
}: {
  owner?: string;
  dirName?: string;
  title?: string;
  /** Render the list without the card shell (inside the drawer). */
  bare?: boolean;
}) {
  const params = owner && dirName ? { owner, dirName } : {};
  const changes = useQuery({
    queryKey: registryKeys.changes(params),
    queryFn: () => registryApi.listChanges(params),
    refetchInterval: (q) => (q.state.data?.some((c) => c.status === 'pr_open') ? 60_000 : false),
  });

  const body = changes.isLoading ? (
    <p className={styles.empty}>Loading…</p>
  ) : !changes.data || changes.data.length === 0 ? (
    <p className={styles.empty}>
      {owner ? 'No pull requests for this skill yet.' : 'No skill changes proposed yet. Edit or upload a skill to open the first one.'}
    </p>
  ) : (
    <div className={styles.list}>
      {changes.data.map((c) => (
        <div key={c.id} className={styles.row}>
          <ChangeStatusPill change={c} />
          <span>
            <span className={styles.rowTitle}>{c.title}</span>
            <span className={styles.rowMeta}>
              {CHANGE_KIND_LABELS[c.kind]} · <code>{c.owner}/{c.dirName}</code> · {formatDate(c.createdAt)}
              {c.mergedAt ? ` · merged ${formatDate(c.mergedAt)}` : ''}
            </span>
          </span>
          {c.prUrl ? (
            <a className={styles.link} href={c.prUrl} target="_blank" rel="noreferrer">
              View PR ↗
            </a>
          ) : (
            <span />
          )}
        </div>
      ))}
    </div>
  );

  if (bare) return body;
  return <Card title={title}>{body}</Card>;
}
